import { Directive, ElementRef, Inject, Input, NgZone, Optional, ViewContainerRef, NgModule, OnChanges, SimpleChanges,
    HostBinding } from '@angular/core';
import {
    MAT_TOOLTIP_DEFAULT_OPTIONS, MAT_TOOLTIP_SCROLL_STRATEGY, MatTooltip, MatTooltipDefaultOptions, MatTooltipModule,
} from '@angular/material/tooltip';
import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { Overlay, ScrollDispatcher } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { coerceNumberProperty, coerceBooleanProperty } from '@angular/cdk/coercion';

@Directive({
    selector: '[euiTooltip]',
    exportAs: 'euiTooltip',
})
export class EuiTooltipDirective extends MatTooltip implements OnChanges {
    // TYPECLASS
    @Input()
    get euiTooltipPrimary() { return this._euiTooltipPrimary; }
    set euiTooltipPrimary(value) { this._euiTooltipPrimary = coerceBooleanProperty(value); }
    _euiTooltipPrimary = false;

    @Input()
    get euiTooltipSecondary() { return this._euiTooltipSecondary; }
    set euiTooltipSecondary(value) { this._euiTooltipSecondary = coerceBooleanProperty(value); }
    _euiTooltipSecondary = false;

    @Input()
    get euiTooltipInfo() { return this._euiTooltipInfo; }
    set euiTooltipInfo(value) { this._euiTooltipInfo = coerceBooleanProperty(value); }
    _euiTooltipInfo = false;

    @Input()
    get euiTooltipSuccess() { return this._euiTooltipSuccess; }
    set euiTooltipSuccess(value) { this._euiTooltipSuccess = coerceBooleanProperty(value); }
    _euiTooltipSuccess = false;

    @Input()
    get euiTooltipWarning() { return this._euiTooltipWarning; }
    set euiTooltipWarning(value) { this._euiTooltipWarning = coerceBooleanProperty(value); }
    _euiTooltipWarning = false;

    @Input()
    get euiTooltipDanger() { return this._euiTooltipDanger; }
    set euiTooltipDanger(value) { this._euiTooltipDanger = coerceBooleanProperty(value); }
    _euiTooltipDanger = false;

    @Input()
    get euiTooltipAccent() { return this._euiTooltipAccent; }
    set euiTooltipAccent(value) { this._euiTooltipAccent = coerceBooleanProperty(value); }
    _euiTooltipAccent = false;

    @Input()
    get euiTooltipRounded() { return this._euiTooltipRounded; }
    set euiTooltipRounded(value) { this._euiTooltipRounded = coerceBooleanProperty(value); }
    _euiTooltipRounded = false;

    @Input()
    get showDelay() { return this._showDelay; }
    set showDelay(value) { this._showDelay = coerceNumberProperty(value); }
    _showDelay = 0;

    @Input()
    get hideDelay() { return this._hideDelay; }
    set hideDelay(value) { this._hideDelay = coerceNumberProperty(value); }
    _hideDelay = 0;

    @Input()
    get isDisabled() { return this._isDisabled; }
    set isDisabled(value) { this._isDisabled = coerceBooleanProperty(value); }
    _isDisabled = false;

    @Input()
    get euiTooltipArrows() { return this._euiTooltipArrows; }
    set euiTooltipArrows(value) { this._euiTooltipArrows = coerceBooleanProperty(value); }
    _euiTooltipArrows = true;

    @Input() position: ('left' | 'right' | 'above' | 'below' | 'before' | 'after') = 'above';

    @Input() content: ('left' | 'right' | 'center' | 'justify') = 'center';

    @Input()
    get euiTooltip() {
        return this.message;
    }
    set euiTooltip(value: string) {
        this.tooltipClass = 'eui-tooltip';
        this.message = value;
    }

    @HostBinding('class')
    get cssClasses() {
        return [
            'eui-tooltip eui-tooltip--' + this.position + ' eui-tooltip--content-' + this.content,
            this.euiTooltipPrimary ? 'eui-tooltip--primary' : '',
            this.euiTooltipSecondary ? 'eui-tooltip--secondary' : '',
            this.euiTooltipInfo ? 'eui-tooltip--info' : '',
            this.euiTooltipSuccess ? 'eui-tooltip--success' : '',
            this.euiTooltipWarning ? 'eui-tooltip--warning' : '',
            this.euiTooltipDanger ? 'eui-tooltip--danger' : '',
            this.euiTooltipAccent ? 'eui-tooltip--accent' : '',
            this.euiTooltipRounded ? 'eui-tooltip--rounded' : '',
            this.euiTooltipArrows ? 'eui-tooltip--arrows' : '',
            this.disabled ? 'eui-tooltip--disabled' : '',
        ].join(' ').trim();
    }
    @Input()
    get always() { return this._always; }
    set always(value) { this._always = coerceBooleanProperty(value); }
    _always = false;

    constructor(
        _overlay: Overlay,
        _elementRef: ElementRef,
        _scrollDispatcher: ScrollDispatcher,
        _viewContainerRef: ViewContainerRef,
        _ngZone: NgZone,
        _platform: Platform,
        _ariaDescriber: AriaDescriber,
        _focusMonitor: FocusMonitor,
        @Inject(MAT_TOOLTIP_SCROLL_STRATEGY) _scrollStrategy: any,
        @Optional() _dir: Directionality,
        @Optional() @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS)
        _defaultOptions: MatTooltipDefaultOptions,
        ) {
        super(
            _overlay,
            _elementRef,
            _scrollDispatcher,
            _viewContainerRef,
            _ngZone,
            _platform,
            _ariaDescriber,
            _focusMonitor,
            _scrollStrategy,
            _dir,
            _defaultOptions,
        );
    }

    ngOnChanges(c: SimpleChanges) {
        if (c && c.isDisabled) {
            this.disabled = c.isDisabled.currentValue;
        }

        if (c) {
            this.tooltipClass = [
                'eui-tooltip eui-tooltip--' + this.position + ' eui-tooltip--content-' + this.content,
                this.euiTooltipPrimary ? 'eui-tooltip--primary' : '',
                this.euiTooltipSecondary ? 'eui-tooltip--secondary' : '',
                this.euiTooltipInfo ? 'eui-tooltip--info' : '',
                this.euiTooltipSuccess ? 'eui-tooltip--success' : '',
                this.euiTooltipWarning ? 'eui-tooltip--warning' : '',
                this.euiTooltipDanger ? 'eui-tooltip--danger' : '',
                this.euiTooltipAccent ? 'eui-tooltip--accent' : '',
                this.euiTooltipRounded ? 'eui-tooltip--rounded' : '',
                this.euiTooltipArrows ? 'eui-tooltip--arrows' : '',
            ].join(' ').trim();
        }
    }
}

@NgModule({
    imports: [CommonModule, MatTooltipModule],
    exports: [EuiTooltipDirective],
    declarations: [EuiTooltipDirective],
})
export class EuiTooltipDirectiveModule {}
